// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DropdownOption, RouteParamsWithLanguage } from "../../../../../../types/screensTypes";
import Button from "../../../../../../components/ui/Button";
import ImageList from "../../../../../../components/images/ImageList";
import { saveZone, getZonesAndRooms, getZoneById } from "../../../../../../api/museum";
import Dropdown from "../../../../../../components/common/Dropdown";
import { FileInput } from "../../../../../../components/common/FileInput";
import NumberInput from "../../../../../../components/common/NumberInput";
import TextInput from "../../../../../../components/ui/TextInput";
import { useTrackCreation } from "../../../../../../context/TrackCreationContext";
import { uploadFileToS3Complete } from "../../../../../../api/s3";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../../../../../utils/image";
import ImageSpecsBadges from "../../../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../../../hooks/useImageValidation";
import roomEdit from "../Zones/RoomEdit.module.css";
import stepStyles from "../../../createStep.module.css";

const ZoneInfo: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { trackData } = useTrackCreation();
    const { zoneId, language } = useParams<RouteParamsWithLanguage>();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [rooms, setRooms] = useState<DropdownOption[]>([]);
    const [zoneName, setZoneName] = useState<string>("");
    const [position, setPosition] = useState<number>(0);
    const [picture, setPicture] = useState<string | null>(null);
    const [selectedRoom, setSelectedRoom] = useState<string | null>(null);
    const imageValidation = useImageValidation({
        type: "zoneThumb",
        imageUrl: picture || undefined,
    });

    const saveZoneAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !zoneId || !language || !selectedRoom) return;

        await saveZone({
            token,
            trackId,
            zoneId,
            language,
            name: zoneName,
            picture: picture ? picture : undefined,
            position,
            subPlanId: selectedRoom ? selectedRoom : undefined,
        });
        // Replace the current path (matching /zone-[^/]+/...) with "/museum-architecture"
        const newPath = location.pathname.replace(/\/zone-[^/]+\/.+$/, "/museum-architecture");
        navigate(newPath);
    };

    const getZonesAndRoomsByTrackId = async (trackId: string): Promise<void> => {
        const token = localStorage.getItem("token");

        if (!token || !trackId) return;
        const response = await getZonesAndRooms({ token, trackId });
        if (response) {
            const data = response;
            if (data) {
                const roomsList = data.subPlans;
                const roomsForDropdown: DropdownOption[] = roomsList
                    .sort((a: any, b: any) => a.floorNumber - b.floorNumber)
                    .map((room: any) => ({
                        label: room.internalName?.trim() || `${t("floorNumber")} ${room.floorNumber}`,
                        value: room.id,
                    }));
                setRooms(roomsForDropdown);
            }
        }
    };

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getZonesAndRoomsByTrackId(trackData.trackId);
        }
    }, [trackData.trackId]);

    const getZoneInfo = async (trackId: string, zId: string, language: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !trackId || !zId || !language) return;

        const zoneInfo = await getZoneById({
            token,
            trackId,
            zoneId: zId,
            language,
        });
        // Set the selected room from the subplan ID
        setSelectedRoom(zoneInfo?.subPlansId);
        const localizedInfo = zoneInfo?.subPlansZonesLocalized;
        const pos = zoneInfo?.position;
        setPosition(pos);
        const photo = zoneInfo?.url;
        setPicture(photo);
        if (localizedInfo && localizedInfo.length > 0) {
            const name = localizedInfo[0].name;
            setZoneName(name);
        }
    };

    useEffect(() => {
        if (zoneId && zoneId !== "" && zoneId !== "null" && trackData.trackId && language) {
            getZoneInfo(trackData.trackId, zoneId, language);
        }
    }, [zoneId, trackData.trackId, language]);

    const handleFileSelected = async (files: File | File[]) => {
        const file = Array.isArray(files) ? files[0] : files; // Toujours prendre un seul fichier

        if (file) {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            await imageValidation.validateImage(file);

            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true, // Public files for general uploads
                    isFolder: false, // No folder structure for general uploads
                },
                token,
            )
                .then((result: string) => {
                    setPicture(result);
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    };

    const setPicturesFromList = (pictures: string[]): void => {
        if (pictures && pictures.length > 0) {
            setPicture(pictures[0]);
        } else {
            setPicture(null);
        }
    };

    return (
        <div className={`experience-room-edit ${roomEdit.root}`}>
            <h2 className={`experience-room-t ${roomEdit.title}`}>{t("insertUpdateYourZoneName")}</h2>

            <h3 className={`experience-room-h ${roomEdit.heading}`}>{t("roomName")}</h3>
            <TextInput
                placeholder={t("title")}
                value={zoneName}
                size="small"
                onChange={(e) => setZoneName(e.target.value)}
            />

            <h3 className={`experience-room-h experience-room-h--gap ${roomEdit.heading} ${roomEdit.headingGap}`}>
                {t("floorSelection")}
            </h3>
            <Dropdown
                label=""
                value={selectedRoom}
                options={rooms}
                onChange={(value: string) => setSelectedRoom(value)}
                placeholder={t("pleaseSelectAPlan")}
                loading={false}
                error={false}
                isWithoutMargin
            />

            <h3 className={`experience-room-h experience-room-h--gap ${roomEdit.heading} ${roomEdit.headingGap}`}>
                {t("inWhichPositionShouldThisCategoryAppear")}
            </h3>
            <NumberInput
                placeholder={t("selectNumber")}
                value={position}
                size="medium"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPosition(Number(e.target.value))}
            />

            <h3 className={`experience-room-sec ${roomEdit.section}`}>{t("requirements")}</h3>
            <ImageSpecsBadges spec="zoneThumb" warnings={imageValidation.warnings} />
            <p className={`experience-room-req ${roomEdit.req}`}>
                ✕ {t("noPhotographerWatermarksLogosOrReadableLicensePlates")}
            </p>

            <div className={stepStyles.fileUpload}>
                <FileInput
                    onFileSelected={handleFileSelected}
                    showButton={true}
                    placeholder={t("dragYourPhotosIntoTheAreaBelowOrSelectUploadPhotos")}
                    acceptedFileTypes={IMAGE_FORMAT}
                    maxSize={IMAGE_MAX_SIZE_200_KB}
                    maxSizeLabel={t("imageMaxSize200kb")}
                />
            </div>

            <div className="photo-gallery">
                <ImageList
                    pictures={picture ? [picture] : []}
                    setPictures={(pics: string[]) => setPicturesFromList(pics)}
                />
            </div>

            <div className={`${stepStyles.stepButton} ${roomEdit.actions}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveZoneAPI}
                />
            </div>
        </div>
    );
};

export default ZoneInfo;
